import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchPublishedGatings } from "actions/gating";
import { fetchPage, searchPages } from "actions/pages";
import {
  createGatingConfig, updateGatingConfig, deleteGatingConfig
} from 'actions/storyboard';

import GatingConfigurations from "components/gatings";
import storyboardRoleService from "services/storyboardRoles.service";

import _ from 'lodash';
import shortid from "shortid";
import cx from 'classnames';

import {
  Button,
  Dropdown,
  Form,
} from "react-bootstrap";

import { getBaseUrl } from "services/util";
import { useRouteMatch } from "react-router-dom/cjs/react-router-dom";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import InfiniteScroll from "components/InfiniteScroll";

import { Property } from "components/Editor/components/properties";
import styles from './websiteSettings.module.scss';

export const ContentGating = ({ storyboard, isGatingSettings, gatingInEditor=false }) => {
  const dispatch = useDispatch();

  const gatingConfigs = useSelector((state) => _.get(state, "storyboards.storyboard.item.gatingConfigurations", [])) || [];

  const isSmartEnabled = gatingConfigs?.find((gatingConfig) => gatingConfig.smart && gatingConfig.enabled);

  useEffect(() => {
    dispatch(fetchPublishedGatings({
      storyboard: storyboard.id,
      published: true
    }, 0, 50));
  }, [storyboard.id]);

    const gatingStates = useMemo(() => {
    if (!gatingConfigs) return { autoGating: null, gatings: [] };

    let autoGating = null;
    const gatings = [];
  
    for (const config of gatingConfigs) {
      if (config?.autoGating && !autoGating) {
        autoGating = config;
      } else {
        gatings.push(config);
      }
    }

    if (!autoGating) {
      const request = {
        uid: shortid(),
        name: 'Auto Gating',
        enabled: false,
        optional: false,
        smart: false,
        autoGating: true,
        trigger: null,
        target: null,
        type: 'AUTO_GATING'
      };
  
      dispatch(createGatingConfig(request));
    }

    return { autoGating, gatings };
  }, [gatingConfigs]);

  useEffect(() => {
    fetchPages();

    if (storyboard.page && !isGatingSettings) {
      if (storyboard.page && storyboard.type === 'INTERACTIVE_CONTENT') {
        dispatch(fetchPage(storyboard.page.id));
      }
    }
  }, []);

  const createNewGating = () => {
    var request = {
      uid: shortid(),
      name: 'Untitled Gating',
      enabled: false,
      optional: false,
      trigger: { type: 'TIME', value: 10 },
      target: null
    }

    dispatch(createGatingConfig(request));
  }

  const fetchPages = () => {
    if (storyboard && storyboard.type !== 'INTERACTIVE_CONTENT') {
      var request = {
        storyboard: storyboard.id
      }

      dispatch(searchPages(request, 0, 50));
    }
  }

  const handleUpdate = (id, request) => {
    dispatch(updateGatingConfig(id, request));
  }

  const handleDelete = (id) => {
    dispatch(deleteGatingConfig(id)); 
  }

  return (
    <div
      id="contentGating"
      className={cx(styles.gatingContainer)}
      data-style="gatingContainer"
    >
      <div className={styles.gatingWrapper} data-style="gatingWrapper">
        {!isGatingSettings && (
          <div className={styles.headerSection}>
            <SiteSettingHeader title="Gating Triggers" description="" />
            <div className={styles.newGatingSection}>
              <Button
                disabled={!storyboardRoleService.canEditStoryboard(storyboard)}
                variant="secondary"
                onClick={createNewGating}
                className={styles.createGatingButton}
              >
                <i className={cx("cleverstory-plus", styles.addIcon)} />
                New Gating
              </Button>
            </div>
          </div>
        )}

        <div
          className={styles.gatingConfigurationsWrapper}
          data-style="gatingConfigurationsWrapper"
        >
          {gatingConfigs.length === 0 && (
            <div className={styles.emptySmartGatingContainer}>
              <div className={styles.emptyMessage}>
                <div className={styles.errorMessageContent}>
                  <div className={styles.errorMessageTitle}>
                    <h2
                      className={cx(styles.heading, styles.gatingHeading)}
                    >
                      Weather forecast
                    </h2>
                  </div>
                  <div
                    className={cx(
                      styles.errorMessageSubText,
                      styles.gatingErrorMessageSubText
                    )}
                  >
                    <span>
                      Clear skies and <br /> empty screens
                    </span>
                  </div>
                </div>
                <img
                  className={cx(styles.emptySmartGatingImage)}
                  src=""
                  alt="emptyMedia_image"
                />
              </div>
            </div>
          )}

          {gatingStates.autoGating?.uid &&
            <AutoGating
              key={gatingStates.autoGating.uid}
              gatingConfig={gatingStates.autoGating}
              onUpdate={handleUpdate}
              gatingInEditor={gatingInEditor}
            />
          }

          {gatingStates.gatings?.map((gatingConfig) => (
            <React.Fragment key={gatingConfig.id}>
              <GatingConfigurations
                gatingConfig={gatingConfig}
                storyboard={storyboard}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                type={"page"}
                isSmartEnabled={isSmartEnabled}
                className={styles.gatingContent}
              />
              <hr className={styles.gatingSeperator} />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

const SiteSettingHeader = ({ title, description }) => {

  return (
    <header className={styles.siteSettingsHeader}>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.description}>{description}</p>
    </header>
  )
}

const AutoGating = ({
  gatingConfig,
  onUpdate,
}) => {

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const gatingScreen = _.get(gatingConfig, 'gating');
  const uid = _.get(gatingConfig, 'uid');
  const enabled = _.get(gatingConfig, 'enabled');
  let trigger = _.get(gatingConfig, 'trigger');
  const conversionType = _.get(gatingConfig, 'conversionType');
  
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  
  const gatingsState = useSelector(state => _.get(state, 'gatings.published.items', []));
  const loading = useSelector(state => _.get(state, 'gatings.published.loading', false));
  const fetched = useSelector(state => _.get(state, 'gatings.published.fetched', false));
  const pagination = useSelector(state => _.get(state, 'gatings.published.pagination', {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const gatings = useMemo(() => {
    return gatingsState?.filter((gating) => gating.progressive && gating.progressiveGatingClusters?.length > 1)
  }, [gatingsState]);

  const [selectedGating, setSelectedGating] = useState(null);
  const [toggleOn, setToggleOn] = useState(enabled);
  const [animated, setAnimated] = useState(false);

  const borderRef = useRef(null);
  const loadingStarted = useRef(false);

  const gatingScreenProgressive = gatingScreen?.progressive && gatings.length > 0;

  const dispatch = useDispatch();

  useEffect(() => {
    if (!enabled) setSelectedGating(null);
  }, [enabled]);

  useEffect(() => {
    if(loading) {
      loadingStarted.current = loading;
    } else {
      if(fetched) {
        loadingStarted.current = true;
      } else {
        loadingStarted.current = false;
      }
    }
  }, [loading, fetched]);

  useEffect(() => {
    if (!loadingStarted.current) return;

    if (!gatingScreenProgressive) {
      onUpdate(uid, {
        smart: false,
        autoGating: true,
        trigger: null,
        optional: false,
        enabled: false,
        gating: null,
        conversionType: null,
      });
      setSelectedGating(null);
    }
  }, [loadingStarted, uid, gatingScreenProgressive]);

  useEffect(() => {
    if (gatingScreen) {
      setSelectedGating(gatingScreen.name);
    } else {
      onUpdate(uid, {
        ...trigger,
        enabled: false,
      });
      setSelectedGating(null);
      setToggleOn(false);
    }
  }, [gatingScreen]);

  useEffect(() => {
    const border = borderRef.current;
    if (!border) return;
  
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setAnimated(true);
        observer.disconnect();
      }
    });
  
    observer.observe(border);
  
    return () => observer.disconnect();
  }, []);

  const handleFetchMappings = (page) => {
    dispatch(fetchPublishedGatings({
      storyboard: storyboard.id,
      published: true
    }, page, 50));
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchMappings(nextPage);
  }

  const handleGatingScreen = (data) => {
    var selected = gatings.find(item => item.id === data);
    if (selected) {
      onUpdate(uid, { gating: selected });
    }
  }

  const handleConversionType = (conversionType) => {
    onUpdate(uid, {
      ...trigger,
      conversionType,
    });
  }

  const renderScreenOption = (selectedData) => {
    return (
      <span className={styles.optionName}>
        <TruncatedTextWithTooltip lines={2} maxWidth={150} content={selectedData} placement="top" />
      </span>
    )
  }

  const handleEditGating = (e) => {
    e.stopPropagation();

    if (!gatingScreen) {
      return;
    }

    const win = window.open(`${baseUrl}/gating/${gatingScreen.id}`, "_blank");
    win.focus();
  }

  const toggleHandler = () => {
    setToggleOn(!toggleOn);

    let request = {
      ...trigger,
      autoGating: false,
      smart: false,
    };
    if(!toggleOn) {
      request = {
        trigger : null,
        smart: false,
        autoGating: true,
        optional: true,
        enabled: true,
      };
    } else {
      request = {
        smart: false,
        autoGating: true,
        trigger: null,
        optional: false,
        enabled: false,
        gating: null,
        conversionType: null
      };
      setSelectedGating(null);
    }
    onUpdate(uid, request);
  };

  return (
    <div className={cx(styles.autoGatingWrapper, { [styles.showConversion]: !!toggleOn }, { [styles.showGating]: !!conversionType && toggleOn })}>
      <div className={cx(styles.autoGatingContentBorder, {[styles.autoGatingContentBorderAnimation]: animated })} ref={borderRef} />
      <div className={styles.autoGatingSection}>
        <div className={styles.autoGatingHeaderSection}>
          <div className={styles.autoGatingHeader}>
            <div className={styles.autoGatingHeaderContent}>
              <p className={styles.autoGatingTitle}>Auto Gating</p>
              <p style={{ background: toggleOn ? '#FFC600' : '#E0E0E0', color: '#424242' }} className={cx(styles.autoGatingInActiveBtn, {[styles.autoGatingActiveBtn]: toggleOn })}>
                <span>{toggleOn ? 'ACTIVE' : 'INACTIVE'}</span>
              </p>
            </div>
            <Form.Check
              type="switch"
              id="custom-switch"
              onChange={toggleHandler}
              checked={toggleOn}
              className={styles.toggleSwitch}
            />
          </div>
          <p className={styles.autoGatingDescription}>
            Select the lead capture strategy that fits your campaign goal.
          </p>
        </div>
        <div className={styles.autoGatingContentWrapper}>
          <span style={{ width: '100%', minHeight: '1px', background: '#E0E0E0', marginBlock: '24px' }} />
          <div className={styles.autoGatingContent}>
            {/* Conversion Type Section */}
            <div className={styles.autoGatingConversion}>
              <p className={styles.title}>Conversion Type</p>
              <div className={styles.conversionTypeCard}>
                <div
                  className={cx(
                    styles.radioContainer,
                    { [styles.selectedRadioContainer]: conversionType === 'RICHLEADS' }
                  )}
                  onClick={() => handleConversionType('RICHLEADS')}
                >
                  <Property.RadioButton
                    title=""
                    value={conversionType === 'RICHLEADS'}
                    className={styles.radioBtn}
                  />
                  <label htmlFor="richLeads" className={styles.radioLabel}>
                    <span className={styles.conversionTitle}>Rich Leads</span>
                    <span className={styles.conversionDescription}>
                      Capture high-intent prospects by asking deeper qualification questions after strong engagement.
                    </span>
                  </label>
                </div>
                <div
                  className={cx(
                    styles.radioContainer,
                    { [styles.selectedRadioContainer]: conversionType === 'MORELEADS' }
                  )}
                  onClick={() => handleConversionType('MORELEADS')}
                >
                  <Property.RadioButton
                    title=""
                    value={conversionType === 'MORELEADS'}
                    className={styles.radioBtn}
                  />
                  <label htmlFor="moreLeads" className={styles.radioLabel}>
                    <span className={styles.conversionTitle}>More Leads</span>
                    <span className={styles.conversionDescription}>
                      Capture a larger number of prospects with shorter forms triggered earlier in the journey.
                    </span>
                  </label>
                </div>
                {/* If you intend to list more options, add here. Otherwise, avoid duplicate options. */}
              </div>
            </div>

            <div className={styles.autoGatingScreen}>
              <p className={styles.title}>Choose a Gating Screen</p>
              {/* Gating Screen Selection */}
              <Dropdown className={styles.gatingDropdown}>
                <Dropdown.Toggle>
                  {gatingScreen ? (
                    <span className={styles.selectedName}>
                      <TruncatedTextWithTooltip
                        maxWidth={150}
                        content={selectedGating}
                        placement="top"
                      />
                    </span>
                  ) : (
                    <span className={styles.selectedName}>Select Gating</span>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <InfiniteScroll
                    className={cx(styles.scrollable)}
                    loading={loading}
                    hasMore={hasMoreItems}
                    loadMore={loadMore}
                  >
                    {gatings.map((data, index) => {
                      const activeGatingScreen =
                        gatingScreen && gatingScreen.id === data.id;
                      return (
                        <Dropdown.Item
                          key={index}
                          active={activeGatingScreen}
                          onClick={() => handleGatingScreen(data.id)}
                        >
                          {renderScreenOption(data.name, activeGatingScreen)}
                        </Dropdown.Item>
                      );
                    })}
                  </InfiniteScroll>
                </Dropdown.Menu>
              </Dropdown>

              {/* Edit Gating Button */}
              <Button
                className={styles.autoGatingEditButton}
                variant="info"
                onClick={handleEditGating}
              >
                <i className={cx("cleverstory-edit", styles.editIcon)} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};